/**
 * /review/:tournamentId — lists all hands with hero hole cards, result,
 * and a link to the step-by-step replay. Export `.txt` via Blob.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CardFace } from '@/components/cards/CardFace';
import type { Action, Card, PlayerId } from '@/engine/types';
import type { HandRow } from '@/storage/db';
import { exportTournamentTxt, listHands } from '@/storage/handHistory';
import { blindSeats, type HandRecord, parseHand } from '@/storage/pokerstars';

function downloadBlob(filename: string, text: string): void {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

type HandSummary = {
  heroId: PlayerId;
  heroHoleCards: readonly [Card, Card] | null;
  position: 'SB' | 'BB' | 'BTN' | 'UTG' | 'HJ' | 'CO' | '?';
  result: 'won' | 'lost' | 'folded';
  wonAmount: number;
};

function inferPosition(record: HandRecord): HandSummary['position'] {
  const hero = record.seats.find((s) => s.playerId === record.heroId);
  if (!hero) return '?';
  const { sbSeat, bbSeat } = blindSeats(record.button, record.seats);
  if (hero.seat === record.button) return 'BTN';
  if (hero.seat === sbSeat) return 'SB';
  if (hero.seat === bbSeat) return 'BB';
  // For 6-max: remaining seats assigned by clockwise distance from BTN.
  const orderedSeats = [...record.seats].sort((a, b) => a.seat - b.seat);
  const btnIdx = orderedSeats.findIndex((s) => s.seat === record.button);
  const heroIdx = orderedSeats.findIndex((s) => s.seat === hero.seat);
  const n = orderedSeats.length;
  const clockwiseDistance = (heroIdx - btnIdx + n) % n;
  if (clockwiseDistance === 3) return 'UTG';
  if (clockwiseDistance === 4) return 'HJ';
  if (clockwiseDistance === 5) return 'CO';
  return '?';
}

function summarizeHand(row: HandRow): HandSummary | null {
  try {
    const record = parseHand(row.pokerstarsHH);
    const heroHoleCards = record.holeCardsByPlayer[record.heroId] ?? null;
    const heroPayout = record.payouts
      .filter((p) => p.playerId === record.heroId)
      .reduce((sum, p) => sum + p.amount, 0);
    const heroFolded = record.actions.some(
      (a: Action) => a.type === 'fold' && a.playerId === record.heroId,
    );
    const result: HandSummary['result'] = heroFolded ? 'folded' : heroPayout > 0 ? 'won' : 'lost';
    return {
      heroId: record.heroId,
      heroHoleCards,
      position: inferPosition(record),
      result,
      wonAmount: heroPayout,
    };
  } catch {
    return null;
  }
}

const RESULT_CLASS: Record<HandSummary['result'], string> = {
  won: 'text-success',
  lost: 'text-danger',
  folded: 'text-muted',
};

const RESULT_LABEL: Record<HandSummary['result'], string> = {
  won: '승',
  lost: '패',
  folded: '폴드',
};

export function TournamentReviewRoute() {
  const { tournamentId } = useParams();
  const [hands, setHands] = useState<HandRow[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!tournamentId) return;
    let cancelled = false;
    void (async () => {
      const rows = await listHands(tournamentId);
      if (cancelled) return;
      setHands(rows);
      setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [tournamentId]);

  const summaries = useMemo(() => {
    const out = new Map<string, HandSummary | null>();
    for (const row of hands) out.set(row.id, summarizeHand(row));
    return out;
  }, [hands]);

  const handleExport = useCallback(async () => {
    if (!tournamentId) return;
    setExporting(true);
    try {
      const text = await exportTournamentTxt(tournamentId);
      downloadBlob(`${tournamentId}.txt`, text);
    } finally {
      setExporting(false);
    }
  }, [tournamentId]);

  if (!tournamentId) {
    return (
      <main className="min-h-dvh bg-bg text-fg p-4">
        <p className="text-sm text-muted">잘못된 경로입니다.</p>
        <Link to="/review" className="text-xs text-accent underline">
          ← 리뷰 리스트
        </Link>
      </main>
    );
  }

  return (
    <main
      className="min-h-dvh bg-bg text-fg flex flex-col gap-3 p-4"
      data-testid="tournament-review-route"
    >
      <header className="flex items-center justify-between">
        <div className="flex flex-col">
          <Link to="/review" className="text-[10px] text-muted hover:text-fg">
            ← 리뷰 리스트
          </Link>
          <h1 className="font-mono text-sm">{tournamentId}</h1>
        </div>
        <button
          type="button"
          onClick={() => {
            void handleExport();
          }}
          disabled={!loaded || hands.length === 0 || exporting}
          className="rounded-md bg-accent text-bg text-xs font-semibold px-3 py-1 disabled:opacity-40"
          data-testid="export-txt"
        >
          {exporting ? '내보내는 중…' : '.txt 내보내기'}
        </button>
      </header>
      {!loaded ? (
        <p className="text-sm text-muted">불러오는 중…</p>
      ) : hands.length === 0 ? (
        <p className="text-sm text-muted">저장된 핸드가 없습니다.</p>
      ) : (
        <ul className="flex flex-col gap-1" data-testid="hand-list">
          {hands.map((hand) => {
            const summary = summaries.get(hand.id);
            return (
              <li
                key={hand.id}
                className="rounded-md bg-bg-elev border border-border px-3 py-2 text-xs"
                data-testid={`hand-row-${hand.handNumber}`}
              >
                <Link
                  to={`/review/${encodeURIComponent(tournamentId)}/${hand.handNumber}`}
                  className="flex items-center justify-between gap-2"
                >
                  <span className="font-mono w-10 text-fg">#{hand.handNumber}</span>
                  <span className="text-muted w-10 text-center">L{hand.level}</span>
                  <span className="text-muted w-8 text-center font-mono">
                    {summary?.position ?? '?'}
                  </span>
                  <span className="flex gap-0.5">
                    <CardFace size="sm" card={summary?.heroHoleCards?.[0]} />
                    <CardFace size="sm" card={summary?.heroHoleCards?.[1]} />
                  </span>
                  <span
                    className={`flex-1 text-right font-mono tabular-nums ${
                      summary ? RESULT_CLASS[summary.result] : 'text-muted'
                    }`}
                  >
                    {summary
                      ? summary.result === 'won'
                        ? `+${summary.wonAmount}`
                        : RESULT_LABEL[summary.result]
                      : '—'}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </main>
  );
}
